/**
 * Price Filter Component
 *
 * Dual-handle range slider for price filtering.
 * Figma: Filter Section - Price (Node: 358:18143)
 *
 * Features: Range slider, min/max labels, live price display
 */
"use client";

import { useEffect, useRef, useState } from "react";

import { colors } from "@/styles/design-tokens";

import { FilterCategoryHeading } from "./FilterCategoryHeading";

interface PriceFilterProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  className?: string;
}

export function PriceFilter({ min, max, value, onChange, className = "" }: PriceFilterProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState<"min" | "max" | null>(null);
  const [localValue, setLocalValue] = useState<[number, number]>(value);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Update local value when prop value changes (but not during drag)
  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  const getPercentage = (val: number) => ((val - min) / (max - min)) * 100;

  const handleMouseDown = (handle: "min" | "max") => {
    // Initialize local value to current value when starting to drag
    setLocalValue(value);
    setIsDragging(handle);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const newValue = min + (percentage / 100) * (max - min);
      const roundedValue = Math.round(newValue / 10) * 10; // Round to nearest $10

      // Update local state while dragging (for visual feedback)
      if (isDragging === "min") {
        setLocalValue([Math.min(roundedValue, localValue[1] - 10), localValue[1]]);
      } else {
        setLocalValue([localValue[0], Math.max(roundedValue, localValue[0] + 10)]);
      }
    };

    const handleMouseUp = () => {
      // Only call onChange on mouse up (release) to avoid excessive updates while dragging
      onChange([...localValue]);
      setIsDragging(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, min, max, localValue, onChange]);

  // Use local value while dragging, prop value otherwise
  const displayValue = isDragging ? localValue : value;
  const minPercent = getPercentage(displayValue[0]);
  const maxPercent = getPercentage(displayValue[1]);

  return (
    <div className={className}>
      {/* Heading */}
      <FilterCategoryHeading
        label="PRICE"
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      {/* Slider Container */}
      {isExpanded && (
        <div
          style={{
            marginTop: "11.974px",
            paddingBottom: "20px",
            paddingRight: "8px", // Account for handle overhang on right edge
            paddingLeft: "8px", // Account for handle overhang on left edge
          }}
        >
          {/* Slider Track */}
          <div
            ref={sliderRef}
            style={{
              width: "100%",
              height: "15.996px",
              position: "relative",
              marginTop: "6.986px",
              cursor: "pointer",
            }}
          >
            {/* Background track */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: "2px",
                backgroundColor: colors.divider.light,
                transform: "translateY(-50%)",
              }}
            />

            {/* Active range */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: `${minPercent}%`,
                right: `${100 - maxPercent}%`,
                height: "2px",
                backgroundColor: colors.stroke.strongBlack,
                transform: "translateY(-50%)",
              }}
            />

            {/* Min handle */}
            <div
              onMouseDown={() => handleMouseDown("min")}
              style={{
                position: "absolute",
                top: "0",
                left: `${minPercent}%`,
                width: "15.996px",
                height: "15.996px",
                borderRadius: "50%",
                backgroundColor: colors.stroke.strongBlack,
                border: `2px solid ${colors.background.white}`,
                cursor: "grab",
                transform: "translateX(-50%)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            />

            {/* Max handle */}
            <div
              onMouseDown={() => handleMouseDown("max")}
              style={{
                position: "absolute",
                top: "0",
                left: `${maxPercent}%`,
                width: "15.996px",
                height: "15.996px",
                borderRadius: "50%",
                backgroundColor: colors.stroke.strongBlack,
                border: `2px solid ${colors.background.white}`,
                cursor: "grab",
                transform: "translateX(-50%)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            />
          </div>

          {/* Price Labels */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "13.46px",
            }}
          >
            <span className="text-tag-caps text-ink-secondary">${displayValue[0]}</span>
            <span className="text-tag-caps text-ink-secondary">${displayValue[1]}</span>
          </div>
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          width: "100%",
          height: "0.882px",
          backgroundColor: colors.divider.light,
          marginTop: isExpanded ? "0px" : "0px",
        }}
      />
    </div>
  );
}
